import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import Axios from "axios";
import JSZip from "jszip";
import JSZipUtils from "jszip-utils";
import FileSaver from "file-saver";
import Localizer from "@Scripts/Common/Localizer";
import FormHelper from "@Scripts/Common/FormHelper";
import MediaHelper from "@Scripts/Common/MediaHelper";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";

@Component({ name: "SectionDetail" })
export default class SectionDetail extends Vue {
    public localizer: any = Localizer;
    public isResetFilter: boolean = true;
    public isLoading: boolean = true;
    public isLoadingForm: boolean = false;
    public isDownloading: boolean = false;
    public documents: any[] = [];
    public formData: any = { category: null, type: null, tags: null, language: null, dateStart: null, dateEnd: null };
    public allowedRoles: any[] = [];
    public languages: any[] = [];
    public tagList: any[] = [];
    public selectedTags: string[] = [];
    public selectedFiles: any[] = [];
    public sectionInfo: any = {};
    public footerPages: any[] = [];
    public sectionDocumentCount: number = 0;
    public pageIndex: number = 0;
    public pageCount: number = 0;
    public paginatorIndex: number = 0;
    public paginatorCount: number = 0;
    public bundleMaxSize: number = 0;

    public isAllItemsSelected: boolean = false;

    public isGenerating: boolean = false;
    public bundleIndex: number = 0;
    public zip: any = new JSZip();

    get userContext() {
        return this.$store.getters["accountUserStore/userContext"];
    }

    get isFilterVisible(): boolean {
        return this.$store.getters["myFileSharingStore/isFilterVisible"];
    }

    get shareTokensDocumentIdMasters() {
        return this.$store.getters["myFileSharingStore/shareTokensDocumentIdMasters"];
    }

    get routes() {
        return (<any>window).App.routes;
    }

    @Prop() readonly sectionName: string;

    @Watch("$route") async onRouteChanged(value: any, oldValue: any) {
        this.selectedFiles = [];
        this.getRouteQuery();

        if (value.params.sectionName === oldValue.params.sectionName) {
            await this.fetchData();
        } else {
            await Promise.all([this.fetchData(), this.getFilterData()]);
        }
    }

    public async created() {
        this.getRouteQuery();

        await Promise.all([this.fetchData(), this.getFilterData()]);
        this.isLoading = false;
    }

    private getRouteQuery() {
        const { category, type, tags, language, page, dateStart, dateEnd } = this.$route.query;

        this.formData = {
            category: category ? String(category) : null,
            type: type ? String(type) : null,
            tags: tags ? String(tags) : null,
            language: language ? String(language) : null,
            page: page ? String(page) : null,
            dateStart: dateStart ? String(dateStart) : null,
            dateEnd: dateEnd ? String(dateEnd) : null,
            sectionName: this.sectionName
        };

        if (category || type || tags || language || dateStart || dateEnd) {
            this.isResetFilter = true;
        } else {
            this.isResetFilter = false;
        }

        this.selectedTags = this.formData.tags ? this.formData.tags?.split(',') : [];
    }

    public async getFilterData() {
        const result = (await Axios.post(this.routes.MyBremboPartsFileSharing_GetFilterData, { sectionName: this.sectionName })).data;
        this.sectionInfo = result;
    }

    public async fetchData() {
        this.isLoadingForm = true;
        this.formData.sectionName = this.sectionName;

        this.documents = [];
        const result = (await Axios.post(this.routes.MyBremboPartsFileSharing_GetDocuments, this.formData)).data;

        this.documents = result.documentInfos;
        this.sectionDocumentCount = result.sectionDocumentCount;
        this.bundleMaxSize = result.bundleMaxSize;
        this.pageIndex = result.pageIndex;
        this.pageCount = result.pageCount;
        this.allowedRoles = result.allowedRoles;
        this.tagList = result.tags;
        this.languages = result.languages;

        this.pagination();

        this.isLoadingForm = false;
    }

    public selectedTagsTitle(): string {
        if (this.selectedTags.length === 0) {
            return "...";
        }

        let title = [];
        for (const item of this.selectedTags) {
            title.push(this.sectionInfo?.tags?.find(x => x.value === item)?.title)
        }

        return title.join(", ");
    }

    public async resetFilter() {
        this.$router.push({ name: 'SectionDetail', params: { sectionName: this.sectionName } }).catch(() => { });
    }

    public updateQueryParameters() {
        let queryParams = {
            category: this.formData.category,
            type: this.formData.type,
            tags: this.selectedTags.join(','),
            language: this.formData.language,
            page: this.formData.page,
            dateStart: this.formData.dateStart,
            dateEnd: this.formData.dateEnd
        };

        const searchParams = new URLSearchParams(window.location.search);
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== null) searchParams.set(key, <string>value);

            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] === '' || queryParams[key] === null || (typeof queryParams[key] === 'object' && Object.keys(queryParams[key]).length === 0)) {
                    delete queryParams[key];
                }
            });
        });

        if (Object.entries(queryParams).length === 0 || searchParams.size === 0) {
            this.$router.push({ name: 'SectionDetail', params: { sectionName: this.sectionName } }).catch(() => { });
        } else {
            this.$router.push({ name: 'SectionDetail', params: { sectionName: this.sectionName }, query: queryParams }).catch(() => { });
        }
    }

    public selectItem() {
        this.isAllItemsSelected = false;
    }

    public selectAllItems() {
        this.selectedFiles = [];

        if (!this.isAllItemsSelected) {
            for (let item of this.documents) {
                this.selectedFiles.push(item);
            }

            this.isAllItemsSelected = true;
        }
    }

    public async downloadZip() {
        this.isDownloading = true;

        const bundleSize = this.selectedFiles.reduce((total, obj) => total + obj.fileSize, 0);
        if (bundleSize > this.bundleMaxSize) {
            alert(this.localizer.get("App.FileSharing.DownloadBundleSizeNotValid", MediaHelper.bytesToString(this.bundleMaxSize)));
            return;
        }

        if (this.selectedFiles.length !== 0) {
            const vm = this;
            var promises = [];
            var files = [];
            this.bundleIndex = 0;
            this.isGenerating = true;

            for (var i = 0; i < this.selectedFiles.length; i++) {
                files.push({
                    name: this.selectedFiles[i].name,
                    idMaser: this.selectedFiles[i].idMaster
                });

                promises.push(new Promise((resolve, reject) => {
                    var file = this.selectedFiles[vm.bundleIndex];
                    vm.bundleIndex++;

                    JSZipUtils.getBinaryContent(file.fileUrl, function (err, data) {
                        vm.zip.file(file.name, data);
                        resolve(0);
                    });
                }));
            }

            await Promise.all(promises);
            this.generateZip();
        }

        this.isDownloading = false;
    }

    public generateZip() {
        const vm = this;

        this.zip.generateAsync({ type: "blob" }).then(function (content) {
            FileSaver.saveAs(content, "Files_" + vm.sectionName + ".zip");
            GTagHelper.sendEvent("click", "filesharing_marketing", "bundle");

            vm.isGenerating = false;
        });
    }

    public goToPage(index: number) {
        this.formData.page = index;
        this.updateQueryParameters();
    }

    public goToPrevPage() {
        if (this.pageIndex < 1) {
            return;
        }
        this.formData.page = this.pageIndex - 1;
        this.updateQueryParameters();
    }

    public goToNextPage() {
        if (this.pageIndex >= this.pageCount - 1) {
            return;
        }
        this.formData.page = this.pageIndex + 1;
        this.updateQueryParameters();
    }

    public pagination() {
        this.paginatorCount = Math.ceil(this.pageCount / 4);
        this.paginatorIndex = Math.trunc(this.pageIndex / 4);

        this.footerPages = [...Array(this.pageCount).keys()].slice(this.paginatorIndex * 4, (this.paginatorIndex + 1) * 4);
    }

    public goToPageSelector(type: string) {
        if (type === "plus") {
            this.goToPage(this.footerPages.slice(-1)[0] + 1);
        }

        if (type === "less") {
            this.goToPage(this.footerPages.slice(0)[0] - 1);
        }
    }

    public setFilterVisible() {
        this.$store.commit("myFileSharingStore/setFilterVisible", !this.isFilterVisible);
    }

    public navigateBack() {
        this.$router.push({ name: "SectionList" });
    }

    public async deleteDocument(document: any) {
        if (confirm(this.localizer.get("App.FileSharing.DeleteDocumentConfrim", document.name)) == true) {
            try {
                var documentCodes = [];
                documentCodes.push(document.code);
                await Axios.post(this.routes.MyBremboPartsFileSharingManagement_RemoveDocument, { documentCodes: documentCodes });

                location.reload();
            } catch (error) {
                if (error.response && error.response.status === 400) {
                    this.$store.dispatch("bremboToastStore/onAddToast", { message: FormHelper.getValidationErrors(error.response.data)[0].message, type: "Error" });
                }
            }
        }
    }

    public async deleteDocuments() {
        if (confirm(this.localizer.get("App.FileSharing.DeleteDocumentsConfrim")) == true) {
            try {
                await Axios.post(this.routes.MyBremboPartsFileSharingManagement_RemoveDocument, { documentCodes: this.selectedFiles.map(obj => obj.code) });

                location.reload();
            } catch (error) {
                if (error.response && error.response.status === 400) {
                    this.$store.dispatch("bremboToastStore/onAddToast", { message: FormHelper.getValidationErrors(error.response.data)[0].message, type: "Error" });
                }
            }
        }
    }

    public editDocument(documentCode: string) {
        let files: string[] = [];
        files.push(documentCode);
        this.$store.commit("myFileSharingStore/setDocumentEditVisible", true);
        this.$store.commit("myFileSharingStore/setDocumentCodes", files);
    }

    public multipleEditDocument() {
        if (this.selectedFiles.length !== 0) {
            this.$store.commit("myFileSharingStore/setDocumentEditVisible", true);
            this.$store.commit("myFileSharingStore/setDocumentCodes", this.selectedFiles.map(obj => obj.code));
        }
    }

    public addAllToBundle() {
        if (this.selectedFiles.length !== 0) {
            this.shareTokensDocumentIdMasters.push(...new Set(this.selectedFiles.map(obj => obj.idMaster)));

            this.$store.commit("myFileSharingStore/setShareTokensDocumentIdMasters", [...new Set(this.shareTokensDocumentIdMasters)]);
            this.$store.dispatch("myFileSharingStore/saveShareTokensDocumentIdMasters");
        }
    }

    public removeAllToBundle() {
        if (this.selectedFiles.length !== 0) {
            let filteredFilesIdMaster = this.shareTokensDocumentIdMasters.filter(x => {
                return this.selectedFiles.some(obj => obj.idMaster === x);
            });

            this.$store.commit("myFileSharingStore/setShareTokensDocumentIdMasters", this.shareTokensDocumentIdMasters.filter(item => !filteredFilesIdMaster.includes(item)));
            this.$store.dispatch("myFileSharingStore/saveShareTokensDocumentIdMasters");
        }
    }

    public addToBundle(documentIdMaster: string) {
        if (this.shareTokensDocumentIdMasters.some(x => x === documentIdMaster)) {
            this.$store.commit("myFileSharingStore/setShareTokensDocumentIdMasters", this.shareTokensDocumentIdMasters.filter(x => x !== documentIdMaster));
        } else {
            this.shareTokensDocumentIdMasters.push(documentIdMaster);
        }

        this.$store.dispatch("myFileSharingStore/saveShareTokensDocumentIdMasters");
    }

    public downloadFile() {
        GTagHelper.sendEvent("file_download", "filesharing_marketing", "file");
    }

    public zoomImage(document: any) {
        (<any>window).Fancybox.bind("[data-fancybox='Zoom_" + document.idMaster + "']", {
            dragToClose: false,
            Images: {
                zoom: false,
            },
            Toolbar: {
                display: {
                    left: [],
                    middle: [],
                    right: ['close'],
                },
            }
        });
    }
}